import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import SharedLayout from '../../components/website/SharedLayout';
import api from '../../lib/axios';
import { FileText, Scale, Shield, Cookie } from 'lucide-react';

const LegalPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine slug from URL path if not provided as param
  const getSlugFromPath = () => {
    const path = location.pathname;
    if (path.includes('mentions-legales')) return 'mentions-legales';
    if (path.includes('confidentialite')) return 'confidentialite';
    if (path.includes('cookies')) return 'cookies';
    if (path.includes('cgv')) return 'cgv';
    return paramSlug || 'mentions-legales';
  };

  const slug = getSlugFromPath();

  // Map slug to page ID
  const pageIdMap = {
    'mentions-legales': 'mentions-legales',
    'cgv': 'cgv',
    'confidentialite': 'confidentialite',
    'cookies': 'cookies'
  };

  // Default content for pages
  const defaultContent = {
    'mentions-legales': `# Mentions légales

## Éditeur du site

**MSC L'Agence**
Agence de communication créative

52 Rue des Pétouses
13800 Istres
Siège social — Ne reçoit pas de public

Téléphone : 04 84 49 27 00
Email : contact@agence-msc.fr

## Hébergement

Ce site est hébergé par Emergent Labs.

## Propriété intellectuelle

L'ensemble du contenu de ce site (textes, images, vidéos, logos, etc.) est protégé par le droit d'auteur. Toute reproduction, même partielle, est interdite sans autorisation préalable.

## Crédits

Conception et développement : MSC L'Agence
`,
    'cgv': `# Conditions Générales de Vente

## Article 1 - Objet

Les présentes conditions générales de vente régissent les relations contractuelles entre MSC L'Agence et ses clients.

## Article 2 - Prix

Les prix sont indiqués en euros hors taxes. La TVA applicable est celle en vigueur au jour de la facturation.

## Article 3 - Paiement

Les factures sont payables à réception, sauf accord particulier.

## Article 4 - Propriété intellectuelle

Sauf accord contraire, les créations réalisées restent la propriété de MSC L'Agence jusqu'au paiement intégral.
`,
    'confidentialite': `# Politique de confidentialité

## Données collectées

Nous collectons les données suivantes :
- Nom et prénom
- Adresse email
- Numéro de téléphone
- Données de navigation

## Utilisation des données

Vos données sont utilisées pour :
- Répondre à vos demandes
- Vous envoyer des informations sur nos services
- Améliorer notre site

## Vos droits

Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données.

Contact : contact@agence-msc.fr
`,
    'cookies': `# Politique des cookies

## Qu'est-ce qu'un cookie ?

Un cookie est un petit fichier texte déposé sur votre navigateur lors de la visite d'un site.

## Cookies utilisés

- Cookies essentiels : fonctionnement du site
- Cookies analytiques : mesure d'audience
- Cookies de préférences : mémorisation de vos choix

## Gestion des cookies

Vous pouvez configurer votre navigateur pour refuser les cookies.
`
  };

  const pageIcons = {
    'mentions-legales': <FileText className="w-8 h-8" />,
    'cgv': <Scale className="w-8 h-8" />,
    'confidentialite': <Shield className="w-8 h-8" />,
    'cookies': <Cookie className="w-8 h-8" />
  };

  const pageTitles = {
    'mentions-legales': 'Mentions légales',
    'cgv': 'Conditions Générales de Vente',
    'confidentialite': 'Politique de confidentialité',
    'cookies': 'Politique des cookies'
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const pageId = pageIdMap[slug] || slug;
        const res = await api.get(`/cms/pages/${pageId}`);
        setPage(res.data);
      } catch (error) {
        // Use default content if page not found
        setPage({
          id: slug,
          title: pageTitles[slug] || 'Page légale',
          content: defaultContent[slug] || ''
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  // Simple Markdown to HTML converter
  const renderMarkdown = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    
    lines.forEach((line, index) => {
      // Headers
      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={index} className="text-3xl font-bold text-slate-900 mb-6 mt-8 first:mt-0">
            {line.substring(2)}
          </h1>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={index} className="text-2xl font-semibold text-slate-800 mb-4 mt-6">
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={index} className="text-xl font-medium text-slate-700 mb-3 mt-4">
            {line.substring(4)}
          </h3>
        );
      }
      // Bold text (simple)
      else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <p key={index} className="font-semibold text-slate-900 mb-2">
            {line.substring(2, line.length - 2)}
          </p>
        );
      }
      // List items
      else if (line.startsWith('- ')) {
        currentList.push(
          <li key={`li-${index}`} className="text-slate-600">
            {line.substring(2)}
          </li>
        );
      }
      // Empty line - flush list
      else if (line.trim() === '') {
        if (currentList.length > 0) {
          elements.push(
            <ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1">
              {currentList}
            </ul>
          );
          currentList = [];
        }
        elements.push(<div key={index} className="h-2" />);
      }
      // Regular paragraph
      else if (line.trim()) {
        elements.push(
          <p key={index} className="text-slate-600 mb-3 leading-relaxed">
            {line}
          </p>
        );
      }
    });
    
    // Flush remaining list
    if (currentList.length > 0) {
      elements.push(
        <ul key="final-list" className="list-disc list-inside mb-4 space-y-1">
          {currentList}
        </ul>
      );
    }
    
    return elements;
  };

  const currentTitle = page?.title || pageTitles[slug] || 'Page légale';
  const currentContent = page?.content || defaultContent[slug] || '';

  return (
    <SharedLayout>
      {/* Hero Section - No top margin needed, SharedLayout handles header spacing */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="p-3 bg-cyan-500/20 rounded-xl text-cyan-400">
              {pageIcons[slug] || <FileText className="w-8 h-8" />}
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center">
            {currentTitle}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              </div>
            ) : (
              <div className="prose prose-slate max-w-none">
                {renderMarkdown(currentContent)}
              </div>
            )}
          </div>
        </div>
      </section>
    </SharedLayout>
  );
};

export default LegalPage;
